import { useEffect, useState } from 'react'
import type React from 'react'
import { Icon } from '@/common/ui/Icon'
import { cn } from '@/common/lib/cn/cn'
import './TokenAmount.css'
import { Button } from '@/common/ui/Form/Button'

const LOW_BALANCE_POPUP_KEY = 'token-amount-low-balance-popup-dismissed'
const LOW_BALANCE_THRESHOLD = 100

type LowBalancePopupDismissState = {
    dismissedBalance: number
    peakBalanceSinceDismiss: number
}

const getLowBalancePopupDismissState = (): LowBalancePopupDismissState | null => {
    try {
        const raw = window.localStorage.getItem(LOW_BALANCE_POPUP_KEY)
        if (!raw) {
            return null
        }

        const parsed = JSON.parse(raw) as LowBalancePopupDismissState
        if (
            typeof parsed.dismissedBalance !== 'number' ||
            typeof parsed.peakBalanceSinceDismiss !== 'number' ||
            Number.isNaN(parsed.dismissedBalance) ||
            Number.isNaN(parsed.peakBalanceSinceDismiss)
        ) {
            return null
        }

        return parsed
    } catch {
        return null
    }
}

const saveLowBalancePopupDismissState = (state: LowBalancePopupDismissState) => {
    try {
        window.localStorage.setItem(LOW_BALANCE_POPUP_KEY, JSON.stringify(state))
    } catch {
        // Если localStorage недоступен, просто закрываем попап для текущего рендера.
    }
}

const updatePeakBalanceSinceDismiss = (balance: number) => {
    const state = getLowBalancePopupDismissState()
    if (!state || balance <= state.peakBalanceSinceDismiss) {
        return
    }

    saveLowBalancePopupDismissState({
        ...state,
        peakBalanceSinceDismiss: balance,
    })
}

const shouldShowLowBalancePopup = (balance: number): boolean => {
    const state = getLowBalancePopupDismissState()
    if (!state) {
        return true
    }

    if (balance === 0 && state.dismissedBalance > 0) {
        return true
    }

    if (balance === 0 && state.peakBalanceSinceDismiss > state.dismissedBalance) {
        return true
    }

    if (state.peakBalanceSinceDismiss > state.dismissedBalance) {
        return true
    }

    return false
}

export type TokenAmountProps<T extends React.ElementType = React.ElementType> = {
    value: number | string
    labelBefore?: string
    total?: number
    trial?: number
    onLowBalancePopupAction?: () => void
    isBalanceLoading?: boolean
    as?: T
    className?: string
} & Omit<React.ComponentPropsWithoutRef<T>, 'as' | 'children'>

export const TokenAmount = <T extends React.ElementType = React.ElementType>({
    value,
    labelBefore,
    trial,
    onLowBalancePopupAction,
    isBalanceLoading = false,
    as,
    className,
    ...props
}: TokenAmountProps<T>) => {
    const Component = as || 'div'
    const formattedValue = typeof value === 'number' ? value.toLocaleString('ru-RU') : value
    const numericValue = typeof value === 'number' ? value : Number(value)
    const hasTrialTokens = typeof trial === 'number' && trial > 0
    const lowBalancePopupDescription = hasTrialTokens
        ? 'Сейчас вы расходуете пробный баланс. Докупите токены, чтобы генерации не остановились в самый нужный момент.'
        : 'Докупите токены, чтобы генерации не остановились в самый нужный момент.'
    const [showLowBalancePopup, setShowLowBalancePopup] = useState(false)
    const isLowBalance =
        !!onLowBalancePopupAction &&
        !isBalanceLoading &&
        !Number.isNaN(numericValue) &&
        numericValue < LOW_BALANCE_THRESHOLD

    useEffect(() => {
        if (!isLowBalance || isBalanceLoading || typeof window === 'undefined') {
            setShowLowBalancePopup(false)
            return
        }

        updatePeakBalanceSinceDismiss(numericValue)
        setShowLowBalancePopup(shouldShowLowBalancePopup(numericValue))
    }, [isLowBalance, isBalanceLoading, numericValue])

    const handleCloseLowBalancePopup = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()

        if (typeof window !== 'undefined' && !Number.isNaN(numericValue)) {
            saveLowBalancePopupDismissState({
                dismissedBalance: numericValue,
                peakBalanceSinceDismiss: numericValue,
            })
        }

        setShowLowBalancePopup(false)
    }

    const handleLowBalancePopupAction = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        onLowBalancePopupAction?.()
    }

    const tokenAmount = (
        <Component
            {...props}
            className={cn('token-amount inline-flex items-center', className)}
            aria-label={`Количество токенов: ${formattedValue}`}
        >
            {labelBefore && <span className='tokens-text mr-1'>{labelBefore}</span>}
            <Icon name='tokens' className={cn('w-4 h-4')} />
            <span className='tokens-text'>{formattedValue}</span>
        </Component>
    )

    if (!showLowBalancePopup) {
        return tokenAmount
    }

    return (
        <div className='relative inline-flex'>
            {tokenAmount}
            <div
                className='absolute right-0 top-[calc(100%+0.75rem)] z-50 w-72 rounded-2xl border border-white/10 bg-[#17121f] p-4 text-left shadow-2xl'
                role='status'
                aria-live='polite'
            >
                <button
                    type='button'
                    className='absolute cursor-pointer right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full text-[#aba7af] transition-colors hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#be96ff]'
                    aria-label='Закрыть'
                    onClick={handleCloseLowBalancePopup}
                >
                    ×
                </button>
                <div className='pr-6'>
                    <p className='text-sm font-medium leading-5 text-white'>
                        {numericValue === 0 ? 'Токены закончились' : 'Токены почти закончились'}
                    </p>
                    <p className='mt-2 mb-4 text-sm leading-5 text-[#aba7af]'>{lowBalancePopupDescription}</p>
                    {onLowBalancePopupAction && (
                        <Button type='button' emphasis='accent' onClick={handleLowBalancePopupAction} size='md'>
                            Докупить токены
                        </Button>
                    )}
                </div>
            </div>
        </div>
    )
}
